import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  Checklist as ChecklistIcon,
  DragIndicator as DragIndicatorIcon,
  Home as HomeIcon,
  LocationOn as LocationOnIcon,
  Category as CategoryIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteIcon,
  Save as SaveIcon,
  Hub as HubIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from "@mui/icons-material";
import _ from "lodash";
import { US_STATES } from "../../../data/usStates";
import { CA_COUNTIES } from "../../../data/caCounties";
import { ProvenanceBadge } from "../../../components/component-contact/ContactCard";
import { apiGetSettings } from "../../../graphQL/settings";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import PropertyHistoryCards from "./PropertyHistoryCards";
import PropertyLoansAndRentalCards from "./PropertyLoansAndRentalCards";
import LinkedDocumentsCard from "../../../components/panels/LinkedDocumentsCard";
import Gate from "../../../components/common/Gate";
import { useToggle } from "../../../contexts/FeatureToggleContext";

/* Property overview card sections → feature-toggle flags. */
const OVERVIEW_CARD_FLAGS = {
  property: "property.section.property",
  owner: "property.section.owner",
  statusCategory: "property.section.statusCategory",
  notes: "property.section.notes",
  gis: "property.section.gis",
  brochures: "property.section.brochures",
};

/* ────────────────────────────────────────────────────────────
   Shared styles
   ──────────────────────────────────────────────────────────── */

const SEARCH_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    fontSize: "14px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e0" },
    "&.Mui-focused fieldset": { borderColor: "#4299e1", borderWidth: "1px" },
    "& input": { padding: "12px 14px" },
    "& .MuiSelect-select": { padding: "12px 14px" },
  },
};

const INFO_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    fontSize: "14px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e0" },
    "&.Mui-focused fieldset": { borderColor: "#4299e1", borderWidth: "1px" },
    "& input": { padding: "12px 14px" },
  },
};

const PROPERTY_INFO_CARD_HEADER_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 1.5,
  py: 0.75,
  minHeight: 40,
  backgroundColor: "#E5E7EB",
  borderBottom: "1px solid #CBD5E1",
};

const PROPERTY_INFO_CARD_TITLE_SX = {
  fontWeight: 700,
  fontSize: 12,
  lineHeight: 1.1,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#334155",
};

const CARD_WRAP_SX = {
  borderRadius: 3,
  border: "1px solid rgba(0,0,0,0.10)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
  bgcolor: "background.paper",
  overflow: "hidden",
  mb: 1.5,
};

const COMPACT_CARD_HEADER_SX = PROPERTY_INFO_CARD_HEADER_SX;
const COMPACT_CARD_TITLE_SX = PROPERTY_INFO_CARD_TITLE_SX;

/* ────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────── */

const deepSet = (obj, path, value) => {
  const clone = _.cloneDeep(obj || {});
  const keys = path.split(".");
  let cursor = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    if (cursor[keys[i]] == null || typeof cursor[keys[i]] !== "object") {
      cursor[keys[i]] = {};
    }
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return clone;
};

const formatOverviewAddressHeader = (address = {}) => {
  const street = [address?.streetNo, address?.streetName, address?.unit ? `Unit ${address.unit}` : ""]
    .filter((value) => String(value || "").trim())
    .join(" ")
    .trim();
  const city = String(address?.city || "").trim();
  const state = String(address?.state || "").trim();
  const zip = String(address?.zip || "").trim();
  const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [street, cityStateZip].filter(Boolean).join(", ");
};
const composeZipCode = (zip, zip4) => {
  const a = String(zip || "").replace(/\D/g, "").slice(0, 5);
  const b = String(zip4 || "").replace(/\D/g, "").slice(0, 4);
  if (!a && !b) return "";
  return b ? `${a}-${b}` : a;
};
const splitZipCode = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return {
    zip: digits.slice(0, 5),
    zip4: digits.slice(5, 9),
  };
};

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");
const compactApnNumber = (v, minWidth = 1) => {
  const digits = onlyDigits(v);
  if (!digits) return "";
  const trimmed = digits.replace(/^0+/, "") || "0";
  return trimmed.padStart(minWidth, "0");
};
const groupDigits = (digits, groups) => {
  let i = 0;
  const parts = [];
  for (const g of groups) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + g));
    i += g;
  }
  if (i < digits.length) parts.push(digits.slice(i));
  return parts.filter(Boolean).join("-");
};
const formatAlamedaApn = (value) => {
  const parts = String(value || "").toUpperCase().trim().split(/[^0-9A-Z]+/).filter(Boolean);
  if (parts.length < 3 || parts.length > 4) return String(value || "").trim();
  const first = parts[0].match(/^0*(\d+)([A-Z])?$/);
  if (!first) return String(value || "").trim();
  if (first[2]) {
    return [
      `${compactApnNumber(first[1], 2)}${first[2]}`,
      compactApnNumber(parts[1], 1),
      compactApnNumber(parts[2], 1),
      parts[3] ? compactApnNumber(parts[3], 1) : "",
    ].filter(Boolean).join("-");
  }
  return [
    compactApnNumber(parts[0], 3),
    compactApnNumber(parts[1], 4),
    compactApnNumber(parts[2], 2),
    parts[3] ? compactApnNumber(parts[3], 1) : "",
  ].filter(Boolean).join("-");
};

const APN_RULES_BY_COUNTY = {
  "Alameda County": {
    formatLabel: "###-####-## or ##A-##-##-#",
    format: formatAlamedaApn,
    // Alameda APNs come in multiple valid shapes:
    //   - Standard parcel:    "001-1234-56"     (3-4-2 digits)
    //   - Sub-parcel:         "001-1234-56-7"   (with sub-suffix)
    //   - Book-letter prefix: "01A-0405-007" or "84A-40-27-2"
    //     (the leading book segment can have a trailing letter; later
    //     segments may be 1-3 digits; sub-suffix may follow)
    // Accept any of the above. GIS treats them all as valid.
    isValid: (formatted) => /^(\d{3}-\d{4}-\d{2,3}(-\d{1,3})?|\d{2}[A-Z]-\d{1,4}-\d{1,3}(-\d{1,3})?)$/i.test(formatted),
  },
  "Santa Clara County": {
    formatLabel: "###-##-###",
    format: (value) => groupDigits(onlyDigits(value), [3, 2, 3]),
    isValid: (formatted) => /^\d{3}-\d{2}-\d{3}$/.test(formatted),
  },
  "Contra Costa County": {
    formatLabel: "###-###-###",
    format: (value) => groupDigits(onlyDigits(value), [3, 3, 3]),
    isValid: (formatted) => /^\d{3}-\d{3}-\d{3}$/.test(formatted),
  },
  "San Mateo County": {
    formatLabel: "###-###-###",
    format: (value) => groupDigits(onlyDigits(value), [3, 3, 3]),
    isValid: (formatted) => /^\d{3}-\d{3}-\d{3}$/.test(formatted),
  },
};

const getApnValidation = (county, apnRaw) => {
  const apn = String(apnRaw || "").trim();
  if (!apn) return { formatted: "", isValid: true, helperText: "" };
  if (!county || county === "No County") {
    return { formatted: apn, isValid: true, helperText: "Select county to validate APN format." };
  }
  const rule = APN_RULES_BY_COUNTY[county];
  if (!rule) {
    return { formatted: apn, isValid: true, helperText: "" };
  }
  if (apn.includes(",")) {
    return {
      formatted: apn,
      isValid: false,
      helperText: `APN does not match ${county} format (${rule.formatLabel}).`,
    };
  }
  const formatted = rule.format(apn);
  const normalizedApn = apn.toUpperCase().replace(/\s+/g, "-").replace(/-+/g, "-");
  const isValid = rule.isValid(normalizedApn);
  return {
    formatted,
    isValid,
    helperText: isValid ? "" : `APN does not match ${county} format (${rule.formatLabel}).`,
  };
};

const getPropertyCardState = (_propertyId, cardKey) => {
  try {
    const stored = localStorage.getItem("property:cardState");
    if (!stored) return true;
    const parsed = JSON.parse(stored);
    return parsed[cardKey] ?? true;
  } catch {
    return true;
  }
};

const savePropertyCardState = (_propertyId, cardKey, value) => {
  try {
    const stored = localStorage.getItem("property:cardState");
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[cardKey] = value;
    localStorage.setItem("property:cardState", JSON.stringify(parsed));
  } catch {}
};

const OverviewDragHandle = () => (
  <DragIndicatorIcon sx={{ fontSize: 16, color: "#94A3B8", cursor: "grab", mr: 0.25, flexShrink: 0 }} />
);

/* ────────────────────────────────────────────────────────────
   PropertyOverviewDetailsTab — 5 cards
   ──────────────────────────────────────────────────────────── */

const PropertyOverviewDetailsTab = ({ propertyInfo, updatePropertyInfo, forceOpen = false, allCardsCollapsed, onCollapseChange, setAllSignal, requestSetAllCards }) => {
  const propertyId = propertyInfo?.id;
  const user = useSelector((state) => state.auth.user);
  const settingsDomain = String(user?.domain || "").trim();
  const address = propertyInfo?.identity?.address || {};

  // Per-card visibility flags (feature toggles). Hidden cards are excluded
  // from rendering, the drag order, and the collapse-all reporting.
  const flagOn = {
    property: useToggle(OVERVIEW_CARD_FLAGS.property),
    owner: useToggle(OVERVIEW_CARD_FLAGS.owner),
    statusCategory: useToggle(OVERVIEW_CARD_FLAGS.statusCategory),
    notes: useToggle(OVERVIEW_CARD_FLAGS.notes),
    gis: useToggle(OVERVIEW_CARD_FLAGS.gis),
    brochures: useToggle(OVERVIEW_CARD_FLAGS.brochures),
  };

  useEffect(() => {
    if (propertyInfo?.id && address.state !== "CA") {
      updatePropertyInfo("identity.address.state", "CA");
    }
  }, [propertyInfo?.id, address.state, updatePropertyInfo]);

  const [isPropertyCollapsed, setIsPropertyCollapsed] = useState(
    () => forceOpen ? false : getPropertyCardState(propertyInfo?.id, "property")
  );
  const [isAddressCollapsed, setIsAddressCollapsed] = useState(
    () => forceOpen ? false : getPropertyCardState(propertyInfo?.id, "address")
  );
  const [isStatusCategoryCollapsed, setIsStatusCategoryCollapsed] = useState(
    () => forceOpen ? false : getPropertyCardState(propertyInfo?.id, "statusCategory")
  );
  const [isNotesCollapsed, setIsNotesCollapsed] = useState(
    () => getPropertyCardState(propertyInfo?.id, "notes")
  );
  const [isBrochuresCollapsed, setIsBrochuresCollapsed] = useState(
    () => getPropertyCardState(propertyInfo?.id, "brochures")
  );
  const [isGisCollapsed, setIsGisCollapsed] = useState(
    () => getPropertyCardState(propertyInfo?.id, "gis")
  );

  const [isOwnerCollapsed, setIsOwnerCollapsed] = useState(
    () => forceOpen ? false : getPropertyCardState(propertyInfo?.id, "owner")
  );
  const ALL_OVERVIEW_CARDS = ["property", "owner", "statusCategory", "notes", "gis", "brochures"];

  // Listen for the parent "set all" broadcast and force every card to target.
  useEffect(() => {
    if (!setAllSignal || setAllSignal.target == null) return;
    const t = !!setAllSignal.target;
    setIsPropertyCollapsed(t); savePropertyCardState(propertyInfo?.id, "property", t);
    setIsAddressCollapsed(t); savePropertyCardState(propertyInfo?.id, "address", t);
    setIsStatusCategoryCollapsed(t); savePropertyCardState(propertyInfo?.id, "statusCategory", t);
    setIsNotesCollapsed(t); savePropertyCardState(propertyInfo?.id, "notes", t);
    setIsGisCollapsed(t); savePropertyCardState(propertyInfo?.id, "gis", t);
    setIsBrochuresCollapsed(t); savePropertyCardState(propertyInfo?.id, "brochures", t);
    setIsOwnerCollapsed(t); savePropertyCardState(propertyInfo?.id, "owner", t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAllSignal?.n]);

  const [cardOrder, setCardOrder] = useState(ALL_OVERVIEW_CARDS);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const propertyHeaderTitle = propertyInfo?.name || "Property";
  const addressHeaderTitle = formatOverviewAddressHeader(address) || "Address";
  const propertyCardHeaderTitle = `${propertyHeaderTitle} — ${addressHeaderTitle}`;

  useEffect(() => {
    const loadSettings = async () => {
      if (!settingsDomain) {
        setCategoryOptions([]);
        setStatusOptions([]);
        return;
      }
      setLoadingSettings(true);
      try {
        const [categoryRes, statusRes] = await Promise.all([
          apiGetSettings({ schemaType: "category", type: "property", domain: settingsDomain }),
          apiGetSettings({ schemaType: "status", type: "property", domain: settingsDomain }),
        ]);
        const pickLatestSetting = (settings) => {
          if (!Array.isArray(settings) || settings.length === 0) return null;
          return settings.reduce((latest, current) => {
            const latestTs = Number(latest?.updatedAt ?? latest?.createdAt ?? 0);
            const currentTs = Number(current?.updatedAt ?? current?.createdAt ?? 0);
            return currentTs >= latestTs ? current : latest;
          }, settings[0]);
        };
        const extractSettingDataArray = (setting) => {
          const payload = setting?.data;
          if (Array.isArray(payload)) return payload;
          if (Array.isArray(payload?.data)) return payload.data;
          return [];
        };
        const catSetting = pickLatestSetting(categoryRes?.settings);
        const statusSetting = pickLatestSetting(statusRes?.settings);
        setCategoryOptions(extractSettingDataArray(catSetting).filter((x) => x && x.isActive !== false));
        setStatusOptions(extractSettingDataArray(statusSetting).filter((x) => x && x.isActive !== false));
      } catch (error) {
        console.error("Failed to load property settings:", error);
        setCategoryOptions([]);
        setStatusOptions([]);
      } finally {
        setLoadingSettings(false);
      }
    };
    loadSettings();
  }, [settingsDomain]);

  const handleToggleProperty = useCallback(() => {
    setIsPropertyCollapsed((prev) => {
      const next = !prev;
      savePropertyCardState(propertyId, "property", next);
      return next;
    });
  }, [propertyId]);

  const handleToggleAddress = useCallback(() => {
    setIsAddressCollapsed((prev) => {
      const next = !prev;
      savePropertyCardState(propertyId, "address", next);
      return next;
    });
  }, [propertyId]);

  const handleToggleStatusCategory = useCallback(() => {
    setIsStatusCategoryCollapsed((prev) => {
      const next = !prev;
      savePropertyCardState(propertyId, "statusCategory", next);
      return next;
    });
  }, [propertyId]);

  const handleToggleNotes = useCallback(() => {
    setIsNotesCollapsed((prev) => {
      const next = !prev;
      savePropertyCardState(propertyId, "notes", next);
      return next;
    });
  }, [propertyId]);

  const handleToggleBrochures = useCallback(() => {
    setIsBrochuresCollapsed((prev) => {
      const next = !prev;
      savePropertyCardState(propertyId, "brochures", next);
      return next;
    });
  }, [propertyId]);
  const handleToggleGis = useCallback(() => {
    setIsGisCollapsed((prev) => {
      const next = !prev;
      savePropertyCardState(propertyId, "gis", next);
      return next;
    });
  }, [propertyId]);

  const handleToggleOwner = useCallback(() => {
    setIsOwnerCollapsed((prev) => {
      const next = !prev;
      savePropertyCardState(propertyId, "owner", next);
      return next;
    });
  }, [propertyId]);

  const collapseStateMap = {
    property: isPropertyCollapsed,
    address: isAddressCollapsed,
    owner: isOwnerCollapsed,
    statusCategory: isStatusCategoryCollapsed,
    notes: isNotesCollapsed,
    gis: isGisCollapsed,
    brochures: isBrochuresCollapsed,
  };
  const isStatusCategoryVisible = statusOptions.length > 0 || categoryOptions.length > 0;
  const visibleCardKeys = cardOrder.filter((key) => {
    if (flagOn[key] === false) return false;
    if (key === "statusCategory") return isStatusCategoryVisible;
    return true;
  });
  const localIsDraggable =
    visibleCardKeys.length > 1 && visibleCardKeys.every((key) => collapseStateMap[key]);

  const draggedCardKeyRef = useRef(null);
  const dragOverCardKeyRef = useRef(null);
  const handleCardDragStart = (key) => (e) => {
    draggedCardKeyRef.current = key;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
    e.currentTarget.style.opacity = "0.5";
  };
  const handleCardDragEnter = (key) => {
    if (draggedCardKeyRef.current === key) return;
    dragOverCardKeyRef.current = key;
  };
  const handleCardDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    const fromKey = draggedCardKeyRef.current;
    const targetKey = dragOverCardKeyRef.current;
    draggedCardKeyRef.current = null;
    dragOverCardKeyRef.current = null;
    if (!fromKey || !targetKey || fromKey === targetKey) return;
    setCardOrder((prev) => {
      const next = [...prev];
      const fromIndex = next.indexOf(fromKey);
      const targetIndex = next.indexOf(targetKey);
      if (fromIndex === -1 || targetIndex === -1) return prev;
      const [moved] = next.splice(fromIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };
  const handleCardDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleRemoveFileUrl = (url) => {
    const currentUrls = Array.isArray(propertyInfo?.details?.publicUrls)
      ? propertyInfo.details.publicUrls
      : (Array.isArray(propertyInfo?.identity?.publicUrls) ? propertyInfo.identity.publicUrls : []);
    updatePropertyInfo("details.publicUrls", currentUrls.filter((u) => u !== url));
  };

  useEffect(() => {
    // Report only visible cards so the panel's collapse-all reflects what the
    // user can actually see (hidden cards must not sway "expand/collapse all").
    const states = {};
    if (flagOn.property) states.property = isPropertyCollapsed;
    if (flagOn.owner) states.owner = isOwnerCollapsed;
    if (flagOn.statusCategory && isStatusCategoryVisible) states.statusCategory = isStatusCategoryCollapsed;
    if (flagOn.notes) states.notes = isNotesCollapsed;
    if (flagOn.gis) states.gis = isGisCollapsed;
    if (flagOn.brochures) states.brochures = isBrochuresCollapsed;
    onCollapseChange?.(states);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagOn.property, flagOn.owner, flagOn.statusCategory, flagOn.notes, flagOn.gis, flagOn.brochures, isStatusCategoryVisible, isPropertyCollapsed, isOwnerCollapsed, isStatusCategoryCollapsed, isNotesCollapsed, isGisCollapsed, isBrochuresCollapsed, onCollapseChange]);

  const isDraggable = typeof allCardsCollapsed === "boolean" ? allCardsCollapsed : localIsDraggable;

  const renderPropertyCard = (isCollapsed) => {
    const fileUrls = Array.isArray(propertyInfo?.details?.publicUrls)
      ? propertyInfo.details.publicUrls
      : (Array.isArray(propertyInfo?.identity?.publicUrls) ? propertyInfo.identity.publicUrls : []);
    const currentCounty = address?.county || "No County";
    const apnRaw = propertyInfo?.identity?.apn || "";
    const apnCheck = getApnValidation(currentCounty, apnRaw);
    const allIngestedApns = Array.isArray(propertyInfo?.details?.ingestedApns) ? propertyInfo.details.ingestedApns : [];
    const fmIngestedApns = allIngestedApns.filter((e) => e && typeof e.source === "string" && e.source.toLowerCase().includes("filemaker"));
    return (
      <Box sx={CARD_WRAP_SX}>
        <Box sx={{ ...PROPERTY_INFO_CARD_HEADER_SX, cursor: "pointer" }} onClick={handleToggleProperty} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isPropertyCollapsed); }} title="Double-click to open/close all">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {isDraggable ? <OverviewDragHandle /> : <Box sx={{ width: 16, mr: 0.25, flexShrink: 0 }} />}
            <HomeIcon sx={{ fontSize: 16, color: "#3B82F6" }} />
            <Typography sx={PROPERTY_INFO_CARD_TITLE_SX}>{propertyCardHeaderTitle}</Typography>
          </Box>
          <IconButton
            size="small"
            sx={{ color: "#64748B", p: 0.35 }}
            onClick={(e) => { e.stopPropagation(); handleToggleProperty(); }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
        {!isCollapsed ? (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField fullWidth label="Property Name" value={propertyInfo?.name || ""} onChange={(event) => updatePropertyInfo("name", event.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      disableClearable
                      options={["No County", ...CA_COUNTIES]}
                      value={[ "No County", ...CA_COUNTIES ].includes(address.county) ? address.county : "No County"}
                      onChange={(_, newValue) => updatePropertyInfo("identity.address.county", newValue || "")}
                      isOptionEqualToValue={(option, value) => option === value}
                      slotProps={{
                        popupIndicator: { title: "" },
                        clearIndicator: { title: "" },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="County"
                          variant="outlined"
                          autoComplete="off"
                          name="property-county"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "new-password",
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            ...SEARCH_FIELD_SX,
                            "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": { padding: "0 14px" },
                            "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot .MuiAutocomplete-input": { padding: "12px 0" },
                          }}
                        />
                      )}
                      ListboxProps={{ style: { maxHeight: "240px" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="APN"
                      value={apnRaw}
                      onChange={(event) => updatePropertyInfo("identity.apn", event.target.value)}
                      onBlur={() => {
                        if (apnCheck.formatted && apnCheck.formatted !== apnRaw) {
                          updatePropertyInfo("identity.apn", apnCheck.formatted);
                        }
                      }}
                      variant="outlined"
                      sx={SEARCH_FIELD_SX}
                      InputLabelProps={{ shrink: true }}
                      error={!apnCheck.isValid}
                      helperText={apnCheck.helperText || " "}
                      FormHelperTextProps={{
                        sx: { mt: 0.5, fontSize: 11, color: !apnCheck.isValid ? "#DC2626" : "#64748B" },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ display: "block", fontSize: 11, color: "#475569", fontStyle: "italic", mt: 0.25 }}
                >
                  <span style={{ fontWeight: 600, color: "#334155" }}>FileMaker APN{fmIngestedApns.length > 1 ? "s" : ""}:</span>{" "}
                  {fmIngestedApns.length > 0
                    ? fmIngestedApns.map((entry, i) => (
                        <span key={i} style={{ color: "#111827" }}>
                          {i > 0 && <span style={{ color: "#94A3B8" }}>, </span>}
                          <span style={{ fontFamily: "monospace" }}>{entry.apn}</span>
                          {entry.ingestedAt && (
                            <span style={{ color: "#94A3B8" }}>
                              {" "}({new Date(entry.ingestedAt).toLocaleDateString()})
                            </span>
                          )}
                        </span>
                      ))
                    : <span style={{ color: "#94A3B8" }}>No APN was ingested from FileMaker.</span>}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ borderTop: "1px solid #E2E8F0", mt: 0.25 }} />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Street No" value={address.streetNo || ""} onChange={(e) => updatePropertyInfo("identity.address.streetNo", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Street Name" value={address.streetName || ""} onChange={(e) => updatePropertyInfo("identity.address.streetName", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Unit #" value={address.unit || ""} onChange={(e) => updatePropertyInfo("identity.address.unit", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="City" value={address.city || ""} onChange={(e) => updatePropertyInfo("identity.address.city", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Zip Code"
                      value={composeZipCode(address.zip, address.zip4)}
                      onChange={(e) => {
                        const next = splitZipCode(e.target.value);
                        updatePropertyInfo("identity.address.zip", next.zip);
                        updatePropertyInfo("identity.address.zip4", next.zip4);
                      }}
                      variant="outlined"
                      sx={SEARCH_FIELD_SX}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value="CA"
                      variant="outlined"
                      sx={SEARCH_FIELD_SX}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {fileUrls.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: "#16A34A", fontSize: "11px", fontWeight: 600, mb: 0.5, display: "block" }}>Public Files</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {fileUrls.map((url) => (
                      <Box key={url} sx={{ display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "#F0FDF4", borderRadius: 1, px: 1, py: 0.5 }}>
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 14, color: "#16A34A", flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ flex: 1, fontSize: "11px", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text", userSelect: "all" }}>
                          {url}
                        </Typography>
                        <Tooltip title="Copy URL">
                          <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(url); }} sx={{ p: 0.25, color: "#64748B", "&:hover": { color: "#16A34A" } }}>
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => handleRemoveFileUrl(url)} sx={{ p: 0.25, color: "#9ca3af", "&:hover": { color: "#ef4444" } }}>
                          <DeleteIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : null}
      </Box>
    );
  };


  const renderOwnerCard = (isCollapsed) => {
    const owner = propertyInfo?.details?.ownerRecord || propertyInfo?.identity?.owner || {};
    const hasOwner = !!(owner.firstName || owner.lastName || owner.company || owner.address?.street || (owner.phones || []).length || (owner.emails || []).length);
    const fullName = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
    const ownerAddr = owner.address || {};
    const addrLine = [
      ownerAddr.street,
      [ownerAddr.city, [ownerAddr.state, ownerAddr.zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    ].filter(Boolean).join(", ");
    const titleSuffix = fullName || owner.company || (hasOwner ? "" : "(no owner on file)");
    const source = owner.source || null;

    const updateOwner = (path, value) => updatePropertyInfo(`details.ownerRecord.${path}`, value);

    return (
      <Box sx={CARD_WRAP_SX}>
        <Box sx={{ ...PROPERTY_INFO_CARD_HEADER_SX, cursor: "pointer", borderBottom: isCollapsed ? "none" : "1px solid #CBD5E1" }} onClick={handleToggleOwner} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isOwnerCollapsed); }} title="Double-click to open/close all">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}>
            {isDraggable ? <OverviewDragHandle /> : <Box sx={{ width: 16, mr: 0.25, flexShrink: 0 }} />}
            <PersonIcon sx={{ fontSize: 16, color: "#0EA5E9" }} />
            <Typography sx={PROPERTY_INFO_CARD_TITLE_SX}>
              Owner{titleSuffix ? ` — ${titleSuffix}` : ""}
            </Typography>
            {source ? <ProvenanceBadge source={source} confidence="verified" /> : null}
          </Box>
          <IconButton
            size="small"
            sx={{ color: "#64748B", p: 0.35 }}
            onClick={(e) => { e.stopPropagation(); handleToggleOwner(); }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
        {!isCollapsed ? (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="First Name" value={owner.firstName || ""} onChange={(e) => updateOwner("firstName", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Last Name" value={owner.lastName || ""} onChange={(e) => updateOwner("lastName", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Company" value={owner.company || ""} onChange={(e) => updateOwner("company", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Mailing Street" value={ownerAddr.street || ""} onChange={(e) => updateOwner("address.street", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 220px", minWidth: 180 }}>
                    <TextField fullWidth label="City" value={ownerAddr.city || ""} onChange={(e) => updateOwner("address.city", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Box>
                  <Box sx={{ flex: "0 0 90px", minWidth: 80 }}>
                    <TextField
                      fullWidth
                      select
                      label="State"
                      value={ownerAddr.state || ""}
                      onChange={(e) => updateOwner("address.state", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      sx={SEARCH_FIELD_SX}
                    >
                      <MenuItem value="">Select</MenuItem>
                      {US_STATES.map((st) => (
                        <MenuItem key={st.code || st.name} value={st.code || st.name}>
                          {st.code || st.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ flex: "0 0 120px", minWidth: 110 }}>
                    <TextField
                      fullWidth
                      label="Zip Code"
                      value={composeZipCode(ownerAddr.zip, ownerAddr.zip4)}
                      onChange={(e) => {
                        const next = splitZipCode(e.target.value);
                        updateOwner("address.zip", next.zip);
                        updateOwner("address.zip4", next.zip4);
                      }}
                      variant="outlined"
                      sx={SEARCH_FIELD_SX}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {(owner.phones || []).length > 0 ? (
                    (owner.phones || []).map((p, i) => (
                      <Box key={`p-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" sx={{ width: 50, color: "#64748B", textTransform: "capitalize" }}>{p.label || "phone"}</Typography>
                        <Typography component="a" href={`tel:${(p.number || "").replace(/\D/g, "")}`} sx={{ fontSize: 13, color: "#0F172A", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                          {p.number}
                        </Typography>
                        <ProvenanceBadge source={source} confidence="verified" />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: 11 }}>No phone on file.</Typography>
                  )}
                  {(owner.emails || []).length > 0 ? (
                    (owner.emails || []).map((e, i) => (
                      <Box key={`e-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" sx={{ width: 50, color: "#64748B" }}>email</Typography>
                        <Typography component="a" href={`mailto:${e}`} sx={{ fontSize: 13, color: "#0F172A", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                          {e}
                        </Typography>
                        <ProvenanceBadge source={source} confidence="verified" />
                      </Box>
                    ))
                  ) : null}
                </Box>
              </Grid>
              {source ? (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontSize: 11 }}>
                    Source: {source}
                    {owner.filledAt ? ` · added ${new Date(owner.filledAt).toLocaleDateString()}` : ""}
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderStatusCategoryCard = (isCollapsed) => (
    <Box sx={CARD_WRAP_SX}>
      <Box sx={{ ...PROPERTY_INFO_CARD_HEADER_SX, cursor: "pointer", borderBottom: isCollapsed ? "none" : "1px solid #CBD5E1" }} onClick={handleToggleStatusCategory} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isStatusCategoryCollapsed); }} title="Double-click to open/close all">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isDraggable ? <OverviewDragHandle /> : <Box sx={{ width: 16, mr: 0.25, flexShrink: 0 }} />}
          <CategoryIcon sx={{ fontSize: 16, color: "#8B5CF6" }} />
          <Typography sx={PROPERTY_INFO_CARD_TITLE_SX}>Status & Category</Typography>
        </Box>
        <IconButton
            size="small"
            sx={{ color: "#64748B", p: 0.35 }}
            onClick={(e) => { e.stopPropagation(); handleToggleStatusCategory(); }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
      </Box>
      {!isCollapsed ? (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2.5}>
            {statusOptions.length > 0 && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingSettings}>
                  <InputLabel shrink>Status</InputLabel>
                  <Select
                    value={propertyInfo?.status || propertyInfo?.details?.status || ""}
                    onChange={(e) => {
                      updatePropertyInfo("details.status", e.target.value);
                      updatePropertyInfo("status", e.target.value);
                    }}
                    displayEmpty
                    notched
                    label="Status"
                    sx={SEARCH_FIELD_SX}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {statusOptions
                      .filter((s) => s.isActive !== false)
                      .map((s) => {
                        const statusValue = s.label || s.name || s.value || s.id;
                        return (
                          <MenuItem key={s.id || s.value || s.label} value={statusValue}>{statusValue}</MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {categoryOptions.length > 0 && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingSettings}>
                  <InputLabel shrink>Category</InputLabel>
                  <Select
                    value={propertyInfo?.category || ""}
                    onChange={(e) => updatePropertyInfo("category", e.target.value)}
                    displayEmpty
                    notched
                    label="Category"
                    sx={SEARCH_FIELD_SX}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {categoryOptions
                      .filter((c) => c.isActive !== false)
                      .map((c) => {
                        const categoryValue = c.id || c.value || c.label;
                        return (
                          <MenuItem key={categoryValue} value={categoryValue}>{c.label || c.name || c.value || c.id}</MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>
      ) : null}
    </Box>
  );

  const renderNotesCard = (isCollapsed) => (
    <Box sx={CARD_WRAP_SX}>
      <Box sx={{ ...PROPERTY_INFO_CARD_HEADER_SX, cursor: "pointer" }} onClick={handleToggleNotes} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isNotesCollapsed); }} title="Double-click to open/close all">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isDraggable ? <OverviewDragHandle /> : <Box sx={{ width: 16, mr: 0.25, flexShrink: 0 }} />}
          <NotesIcon sx={{ fontSize: 16, color: "#F59E0B" }} />
          <Typography sx={PROPERTY_INFO_CARD_TITLE_SX}>Notes</Typography>
        </Box>
        <IconButton
            size="small"
            sx={{ color: "#64748B", p: 0.35 }}
            onClick={(e) => { e.stopPropagation(); handleToggleNotes(); }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
      </Box>
      {!isCollapsed ? (
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={20}
            label="Notes"
            value={propertyInfo?.details?.description || ""}
            onChange={(e) => updatePropertyInfo("details.description", e.target.value)}
            variant="outlined"
            sx={{
              ...SEARCH_FIELD_SX,
              "& .MuiInputBase-inputMultiline": { resize: "vertical", overflow: "auto" },
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      ) : null}
    </Box>
  );

  const renderBrochuresCard = (isCollapsed) => {
    const fileUrls = Array.isArray(propertyInfo?.details?.publicUrls)
      ? propertyInfo.details.publicUrls
      : (Array.isArray(propertyInfo?.identity?.publicUrls) ? propertyInfo.identity.publicUrls : []);
    return (
      <Box sx={CARD_WRAP_SX}>
        <Box sx={{ ...PROPERTY_INFO_CARD_HEADER_SX, cursor: "pointer" }} onClick={handleToggleBrochures} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isBrochuresCollapsed); }} title="Double-click to open/close all">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {isDraggable ? <OverviewDragHandle /> : <Box sx={{ width: 16, mr: 0.25, flexShrink: 0 }} />}
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: "#16A34A" }} />
            <Typography sx={PROPERTY_INFO_CARD_TITLE_SX}>Brochures & Public Files</Typography>
          </Box>
          <IconButton
            size="small"
            sx={{ color: "#64748B", p: 0.35 }}
            onClick={(e) => { e.stopPropagation(); handleToggleBrochures(); }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
        {!isCollapsed ? (
          <Box sx={{ p: 2 }}>
            {fileUrls.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {fileUrls.map((url) => (
                  <Box key={url} sx={{ display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "#F0FDF4", borderRadius: 1, px: 1, py: 0.5 }}>
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 14, color: "#16A34A", flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ flex: 1, fontSize: "11px", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text", userSelect: "all" }}>
                      {url}
                    </Typography>
                    <Tooltip title="Copy URL">
                      <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(url); }} sx={{ p: 0.25, color: "#64748B", "&:hover": { color: "#16A34A" } }}>
                        <ContentCopyIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleRemoveFileUrl(url)} sx={{ p: 0.25, color: "#9ca3af", "&:hover": { color: "#ef4444" } }}>
                      <DeleteIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "11px" }}>
                No public files attached yet.
              </Typography>
            )}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderGisCard = (isCollapsed) => {
    const assessed = propertyInfo?.details?.assessed || {};
    const recording = propertyInfo?.details?.recording || {};
    const gis = propertyInfo?.details?.gis || {};
    const gisState = String(gis?.state || "").trim();
    const formatCurrencyDisplay = (value) => {
      if (value === null || value === undefined || value === "") return "";
      if (typeof value === "number" && Number.isFinite(value)) {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
      }
      const numeric = Number(String(value).replace(/[$,\s]/g, ""));
      if (Number.isFinite(numeric)) {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(numeric);
      }
      return String(value);
    };
    const formatDateDisplay = (value) => {
      if (value === null || value === undefined || value === "") return "";
      let dt = null;
      if (typeof value === "number" && Number.isFinite(value)) {
        dt = new Date(value);
      } else {
        const asNum = Number(value);
        if (Number.isFinite(asNum) && String(value).trim().length >= 10) dt = new Date(asNum);
        if (!dt || Number.isNaN(dt.getTime())) dt = new Date(String(value));
      }
      if (!dt || Number.isNaN(dt.getTime())) return String(value);
      return dt.toLocaleDateString("en-US");
    };

    const landValueDisplay = formatCurrencyDisplay(assessed?.landValue);
    const improvementValueDisplay = formatCurrencyDisplay(assessed?.improvementValue);
    const totalNetValueDisplay = formatCurrencyDisplay(assessed?.totalNetValue);
    const assessmentAsOfDisplay = formatDateDisplay(assessed?.asOf);
    const latestDocDateDisplay = formatDateDisplay(recording?.latestDocumentDate);

    const propertyInfoExtras = propertyInfo?.details?.propertyInfo || {};
    const ownerMailing = propertyInfo?.details?.ownerMailing || {};
    const useType = propertyInfo?.details?.useType || "";
    const taxRateArea = propertyInfo?.details?.taxRateArea || "";
    const zoning = propertyInfo?.details?.zoning || "";
    const jurisdiction = propertyInfo?.details?.jurisdiction || "";
    const ownerList = Array.isArray(propertyInfo?.details?.owner)
      ? propertyInfo.details.owner.map((v) => String(v || "").trim()).filter(Boolean)
      : [];
    const ownerDisplay = ownerList.join("; ");
    const numUnits = propertyInfoExtras?.units || propertyInfoExtras?.livingUnits || "";
    const yearBuilt = propertyInfoExtras?.officeYearBuilt || "";
    const buildingSqft = propertyInfoExtras?.officeGrossBuildingArea || propertyInfoExtras?.squareFeet || propertyInfoExtras?.legalAreaSqft || "";
    const landAcres = propertyInfoExtras?.legalAreaAcres || propertyInfoExtras?.acres || propertyInfoExtras?.officeLandArea || "";
    const pricePerSqFt = formatCurrencyDisplay(propertyInfoExtras?.pricePerSqFt);
    const docNumber = recording?.documentNumber || "";
    const mailingFullCityState = [ownerMailing?.city, ownerMailing?.state, ownerMailing?.cityState].filter(Boolean).join(" ");
    const mailingFull = [ownerMailing?.street, ownerMailing?.unit, mailingFullCityState, ownerMailing?.zip].filter(Boolean).join(", ");

    const fld = (label, value, half = true) => {
      if (value === null || value === undefined || value === "") return null;
      return (
        <Grid item xs={12} md={half ? 6 : 12} key={label}>
          <TextField fullWidth label={label} value={String(value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
        </Grid>
      );
    };
    const extraFields = [
      fld("Use Type", useType),
      fld("Units", numUnits),
      fld("Year Built", yearBuilt),
      fld("Building Area (sqft)", buildingSqft),
      fld("Land Area (acres)", landAcres),
      fld("Price / Sq Ft", pricePerSqFt),
      fld("Zoning", zoning),
      fld("Tax Rate Area", taxRateArea),
      fld("Jurisdiction", jurisdiction),
      fld("Document Number", docNumber),
      fld("Owner (per GIS)", ownerDisplay, false),
      fld("Owner Mailing Address", mailingFull, false),
    ].filter(Boolean);
    const rawAttributes = (gis && typeof gis === "object")
      ? (gis?.attributes && typeof gis.attributes === "object" ? gis.attributes : gis)
      : {};
    let rawJson = "";
    try {
      rawJson = rawAttributes ? JSON.stringify(rawAttributes, null, 2) : "";
    } catch {
      rawJson = "";
    }
    return (
      <Box sx={CARD_WRAP_SX}>
        <Box sx={{ ...PROPERTY_INFO_CARD_HEADER_SX, cursor: "pointer" }} onClick={handleToggleGis} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isGisCollapsed); }} title="Double-click to open/close all">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {isDraggable ? <OverviewDragHandle /> : <Box sx={{ width: 16, mr: 0.25, flexShrink: 0 }} />}
            <HubIcon sx={{ fontSize: 16, color: "#0EA5E9" }} />
            <Typography sx={PROPERTY_INFO_CARD_TITLE_SX}>GIS</Typography>
          </Box>
          <IconButton
            size="small"
            sx={{ color: "#64748B", p: 0.35 }}
            onClick={(e) => { e.stopPropagation(); handleToggleGis(); }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
        {!isCollapsed ? (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GIS State"
                  value={gisState || ""}
                  variant="outlined"
                  sx={SEARCH_FIELD_SX}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Land Value" value={landValueDisplay} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Improvement Value" value={improvementValueDisplay} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Total Net Value" value={totalNetValueDisplay} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Assessment As Of" value={assessmentAsOfDisplay} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Latest Document Date" value={latestDocDateDisplay} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Doc Prefix" value={recording?.latestDocumentPrefix || ""} onChange={(e) => updatePropertyInfo("details.recording.latestDocumentPrefix", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Doc Series" value={recording?.latestDocumentSeries || ""} onChange={(e) => updatePropertyInfo("details.recording.latestDocumentSeries", e.target.value)} variant="outlined" sx={SEARCH_FIELD_SX} InputLabelProps={{ shrink: true }} />
              </Grid>
              {extraFields}
              <Grid item xs={12}>
                <TextField
                  key={`gis-raw-${propertyId}`}
                  fullWidth
                  multiline
                  minRows={8}
                  label="GIS Raw Attributes"
                  defaultValue={rawJson}
                  onBlur={(e) => {
                    try {
                      const next = JSON.parse(e.target.value || "{}");
                      updatePropertyInfo("details.gis", { ...(gis || {}), attributes: next });
                    } catch {
                      // invalid JSON on blur — leave persisted value untouched
                    }
                  }}
                  variant="outlined"
                  sx={SEARCH_FIELD_SX}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderCard = (key) => {
    const isCollapsed = collapseStateMap[key];
    switch (key) {
      case "property": return renderPropertyCard(isCollapsed);
      case "owner": return renderOwnerCard(isCollapsed);
      case "statusCategory": return renderStatusCategoryCard(isCollapsed);
      case "notes": return renderNotesCard(isCollapsed);
      case "gis": return renderGisCard(isCollapsed);
      case "brochures": return renderBrochuresCard(isCollapsed);
      default: return null;
    }
  };

  return (
    <>
      {visibleCardKeys.map((key) => (
        <Gate key={key} flag={OVERVIEW_CARD_FLAGS[key]}>
          <Box
            draggable={isDraggable}
            onDragStart={isDraggable ? handleCardDragStart(key) : undefined}
            onDragEnter={isDraggable ? () => handleCardDragEnter(key) : undefined}
            onDragEnd={handleCardDragEnd}
            onDragOver={handleCardDragOver}
          >
            {renderCard(key)}
          </Box>
        </Gate>
      ))}
    </>
  );
};

/* ────────────────────────────────────────────────────────────
   PropertyInfoDetailsTab — Property Info + Amenities cards
   ──────────────────────────────────────────────────────────── */

const FEATURE_SECTIONS = [
  { fieldName: "amenities", inputLabel: "Add Amenity", emptyLabel: "No amenities added." },
  { fieldName: "exteriorFeatures", inputLabel: "Add Exterior Feature", emptyLabel: "No exterior features added." },
  { fieldName: "interiorFeatures", inputLabel: "Add Interior Feature", emptyLabel: "No interior features added." },
  { fieldName: "miscFeatures", inputLabel: "Add Misc Feature", emptyLabel: "No misc features added." },
];

const PropertyInfoDetailsTab = React.memo(
  ({ propertyInfo, updatePropertyInfo, forceOpen = false, allCardsCollapsed, onCollapseChange, setAllSignal, requestSetAllCards }) => {
    const propertyId = propertyInfo?.id;
    const infoFlagOn = useToggle("property.section.info");
    const amenitiesFlagOn = useToggle("property.section.amenities");
    const isAnyFieldFocusedRef = useRef(false);
    const featureInputRefs = useRef({
      amenities: null, exteriorFeatures: null, interiorFeatures: null, miscFeatures: null,
    });

    const normalizeStringList = useCallback((listSource = []) => {
      if (!Array.isArray(listSource)) return [];
      const seen = new Set();
      const normalized = [];
      listSource.forEach((item) => {
        const value = String(item ?? "").trim();
        if (!value) return;
        const dedupeKey = value.toLowerCase();
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        normalized.push(value);
      });
      return normalized;
    }, []);

    const getFeatureListsFromDetails = useCallback(
      (details = {}) => ({
        amenities: normalizeStringList(details?.amenities || []),
        exteriorFeatures: normalizeStringList(details?.exteriorFeatures || []),
        interiorFeatures: normalizeStringList(details?.interiorFeatures || []),
        miscFeatures: normalizeStringList(details?.miscFeatures || []),
      }),
      [normalizeStringList]
    );

    const [featureLists, setFeatureLists] = useState(() =>
      getFeatureListsFromDetails(propertyInfo?.details || {})
    );
    const [isCollapsed, setIsCollapsed] = useState(
      () => forceOpen ? false : getPropertyCardState(propertyInfo?.id, "amenities")
    );
    const [isInfoCollapsed, setIsInfoCollapsed] = useState(
      () => forceOpen ? false : getPropertyCardState(propertyInfo?.id, "info")
    );

    const [infoCardOrder, setInfoCardOrder] = useState(["info", "amenities"]);
    const infoDragItem = useRef(null);
    const infoDragOverItem = useRef(null);
    const allInfoCollapsed = isCollapsed && isInfoCollapsed;

    const handleInfoDragStart = useCallback((e, idx) => {
      infoDragItem.current = idx;
      e.dataTransfer.effectAllowed = "move";
      e.currentTarget.style.opacity = "0.5";
    }, []);
    const handleInfoDragEnter = useCallback((idx) => { infoDragOverItem.current = idx; }, []);
    const handleInfoDragEnd = useCallback((e) => {
      e.currentTarget.style.opacity = "1";
      if (infoDragItem.current === null || infoDragOverItem.current === null || infoDragItem.current === infoDragOverItem.current) return;
      setInfoCardOrder((prev) => {
        const next = [...prev];
        const [removed] = next.splice(infoDragItem.current, 1);
        next.splice(infoDragOverItem.current, 0, removed);
        return next;
      });
      infoDragItem.current = null;
      infoDragOverItem.current = null;
    }, []);
    const handleInfoDragOver = useCallback((e) => { e.preventDefault(); }, []);

    useEffect(() => {
      const states = {};
      if (infoFlagOn) states.info = isInfoCollapsed;
      if (amenitiesFlagOn) states.amenities = isCollapsed;
      onCollapseChange?.(states);
    }, [infoFlagOn, amenitiesFlagOn, isCollapsed, isInfoCollapsed, onCollapseChange]);

    useEffect(() => {
      if (!setAllSignal || setAllSignal.target == null) return;
      const t = !!setAllSignal.target;
      setIsCollapsed(t); savePropertyCardState(propertyId, "amenities", t);
      setIsInfoCollapsed(t); savePropertyCardState(propertyId, "info", t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setAllSignal?.n]);

    const effectiveAllCollapsed = typeof allCardsCollapsed === "boolean" ? allCardsCollapsed : allInfoCollapsed;

    useEffect(() => {
      if (isAnyFieldFocusedRef.current) return;
      const nextFeatureLists = getFeatureListsFromDetails(propertyInfo?.details || {});
      setFeatureLists((prevLists) =>
        _.isEqual(prevLists, nextFeatureLists) ? prevLists : nextFeatureLists
      );
    }, [
      getFeatureListsFromDetails,
      propertyInfo?.details?.amenities,
      propertyInfo?.details?.exteriorFeatures,
      propertyInfo?.details?.interiorFeatures,
      propertyInfo?.details?.miscFeatures,
    ]);

    const handleFeatureFocus = useCallback(() => { isAnyFieldFocusedRef.current = true; }, []);
    const handleFeatureBlur = useCallback(() => {
      setTimeout(() => { isAnyFieldFocusedRef.current = false; }, 120);
    }, []);

    const persistFeatureList = useCallback(
      (fieldName, nextValues) => {
        setFeatureLists((prevLists) => {
          if (_.isEqual(prevLists?.[fieldName], nextValues)) return prevLists;
          return { ...prevLists, [fieldName]: nextValues };
        });
        if (updatePropertyInfo) updatePropertyInfo(`details.${fieldName}`, nextValues);
      },
      [updatePropertyInfo]
    );

    const handleAddFeature = useCallback(
      (fieldName) => {
        const inputElement = featureInputRefs.current[fieldName];
        const nextValue = String(inputElement?.value || "").trim();
        if (!nextValue) return;
        const currentValues = featureLists?.[fieldName] || [];
        const nextValues = normalizeStringList([...currentValues, nextValue]);
        persistFeatureList(fieldName, nextValues);
        if (inputElement) { inputElement.value = ""; inputElement.focus(); }
      },
      [featureLists, normalizeStringList, persistFeatureList]
    );

    const handleRemoveFeature = useCallback(
      (fieldName, indexToRemove) => {
        const currentValues = featureLists?.[fieldName] || [];
        const nextValues = currentValues.filter((_, idx) => idx !== indexToRemove);
        persistFeatureList(fieldName, nextValues);
      },
      [featureLists, persistFeatureList]
    );

    const DragHandle = effectiveAllCollapsed
      ? () => <DragIndicatorIcon sx={{ fontSize: 16, color: "#94A3B8", cursor: "grab", mr: 0.25, flexShrink: 0 }} />
      : () => <Box sx={{ width: 16, mr: 0.25, flexShrink: 0 }} />;

    const renderInfoCard = (key) => {
      if (key === "info") {
        return (
          <Box sx={CARD_WRAP_SX}>
            <Box sx={{ ...COMPACT_CARD_HEADER_SX, cursor: "pointer" }} title="Double-click to open/close all" onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isInfoCollapsed); }} onClick={() => setIsInfoCollapsed((prev) => {
              const next = !prev;
              savePropertyCardState(propertyId, "info", next);
              return next;
            })}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <DragHandle />
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#3B82F6" }} />
                <Typography sx={COMPACT_CARD_TITLE_SX}>Property Info</Typography>
              </Box>
              <IconButton size="small" sx={{ color: "#64748B", p: 0.35 }}>
                {isInfoCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Box>
            {!isInfoCollapsed ? (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Property Type" value={propertyInfo?.details?.propertyType || ""} onChange={(e) => updatePropertyInfo("details.propertyType", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="SubType" value={propertyInfo?.details?.subtype || ""} onChange={(e) => updatePropertyInfo("details.subtype", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Building Class" value={propertyInfo?.details?.buildingClass || ""} onChange={(e) => updatePropertyInfo("details.buildingClass", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Year Built" value={propertyInfo?.details?.yearBuilt ?? propertyInfo?.details?.propertyInfo?.construction_year ?? ""} onChange={(e) => updatePropertyInfo("details.yearBuilt", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Year Renovated" value={propertyInfo?.details?.yearRenovated ?? propertyInfo?.details?.propertyInfo?.renovation_year ?? ""} onChange={(e) => updatePropertyInfo("details.yearRenovated", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Stories" value={propertyInfo?.details?.stories || ""} onChange={(e) => updatePropertyInfo("details.stories", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Units" value={propertyInfo?.details?.units || ""} onChange={(e) => updatePropertyInfo("details.units", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Use Type" value={propertyInfo?.details?.useType || ""} onChange={(e) => updatePropertyInfo("details.useType", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Building Sqft" value={propertyInfo?.details?.squareFoot || ""} onChange={(e) => updatePropertyInfo("details.squareFoot", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Lot Sqft" value={propertyInfo?.details?.lotSquareFoot || ""} onChange={(e) => updatePropertyInfo("details.lotSquareFoot", e.target.value)} variant="outlined" sx={INFO_FIELD_SX} InputLabelProps={{ shrink: true }} />
                  </Grid>
                </Grid>
              </Box>
            ) : null}
          </Box>
        );
      }
      if (key === "amenities") {
        return (
          <Box sx={CARD_WRAP_SX}>
            <Box sx={{ ...COMPACT_CARD_HEADER_SX, borderBottom: isCollapsed ? "none" : "1px solid #E2E8F0", cursor: "pointer" }} title="Double-click to open/close all" onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isCollapsed); }} onClick={() => setIsCollapsed((prev) => {
              const next = !prev;
              savePropertyCardState(propertyId, "amenities", next);
              return next;
            })}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <DragHandle />
                <ChecklistIcon sx={{ fontSize: 16, color: "#10B981" }} />
                <Typography sx={COMPACT_CARD_TITLE_SX}>Amenities List</Typography>
              </Box>
              <IconButton
            size="small"
            sx={{ color: "#64748B", p: 0.35 }}
            onClick={(e) => { e.stopPropagation(); setIsCollapsed((prev) => { const next = !prev; savePropertyCardState(propertyId, "amenities", next); return next; }); }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
            </Box>
            {!isCollapsed ? (
              <Box sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  {FEATURE_SECTIONS.map((section, index) => {
                    const items = featureLists?.[section.fieldName] || [];
                    return (
                      <Box key={section.fieldName}>
                        <TextField
                          inputRef={(element) => { featureInputRefs.current[section.fieldName] = element; }}
                          fullWidth
                          label={section.inputLabel}
                          placeholder="Type value and press Enter"
                          sx={INFO_FIELD_SX}
                          InputLabelProps={{ shrink: true }}
                          onFocus={handleFeatureFocus}
                          onBlur={handleFeatureBlur}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              handleAddFeature(section.fieldName);
                            }
                          }}
                        />
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                          {items.length ? (
                            items.map((item, itemIndex) => (
                              <Chip
                                key={`${section.fieldName}-${item}-${itemIndex}`}
                                label={item}
                                onDelete={() => handleRemoveFeature(section.fieldName, itemIndex)}
                                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                size="small"
                                sx={{ bgcolor: "#F8FAFC" }}
                              />
                            ))
                          ) : (
                            <Typography sx={{ fontSize: 12, color: "#64748B" }}>{section.emptyLabel}</Typography>
                          )}
                        </Box>
                        {index < FEATURE_SECTIONS.length - 1 ? <Divider sx={{ mt: 1.25 }} /> : null}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ) : null}
          </Box>
        );
      }
      return null;
    };

    return (
      <>
        {infoCardOrder.map((key, idx) => {
          const node = (
            <Box
              draggable={effectiveAllCollapsed}
              onDragStart={(e) => effectiveAllCollapsed && handleInfoDragStart(e, idx)}
              onDragEnter={() => effectiveAllCollapsed && handleInfoDragEnter(idx)}
              onDragEnd={handleInfoDragEnd}
              onDragOver={handleInfoDragOver}
            >
              {renderInfoCard(key)}
            </Box>
          );
          return key === "info" ? (
            <Gate key={key} flag="property.section.info">{node}</Gate>
          ) : (
            <Gate key={key} flag="property.section.amenities">{node}</Gate>
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => {
    const prevDetails = prevProps.propertyInfo?.details || {};
    const nextDetails = nextProps.propertyInfo?.details || {};
    if (
      prevProps.propertyInfo?.id === nextProps.propertyInfo?.id &&
      _.isEqual(prevDetails?.amenities, nextDetails?.amenities) &&
      _.isEqual(prevDetails?.exteriorFeatures, nextDetails?.exteriorFeatures) &&
      _.isEqual(prevDetails?.interiorFeatures, nextDetails?.interiorFeatures) &&
      _.isEqual(prevDetails?.miscFeatures, nextDetails?.miscFeatures) &&
      prevProps.updatePropertyInfo === nextProps.updatePropertyInfo &&
      prevProps.allCardsCollapsed === nextProps.allCardsCollapsed &&
      prevProps.setAllSignal?.n === nextProps.setAllSignal?.n
    ) {
      return true;
    }
    return false;
  }
);

/* ────────────────────────────────────────────────────────────
   PropertyDetailPanel — outer wrapper with shell + save bar
   ──────────────────────────────────────────────────────────── */

const PropertyDetailPanel = ({ property, onSave, onCopy, onClose, embedded = false, onDirtyChange = null }) => {
  const [propertyInfo, setPropertyInfo] = useState(property || null);
  const [dirty, setDirty] = useState(false);
  const [collapseStates, setCollapseStates] = useState({});
  // Broadcast for "expand/collapse all" — double-click on any card header
  // bumps `n`; each sub-tab subscribes and forces its cards to `target`.
  const [setAllSignal, setSetAllSignal] = useState({ target: null, n: 0 });
  const requestSetAllCards = useCallback((target) => {
    setSetAllSignal((prev) => ({ target: !!target, n: prev.n + 1 }));
  }, []);

  useEffect(() => {
    if (!property) {
      setPropertyInfo(null);
      setDirty(false);
      return;
    }
    // Re-sync on object identity too -- the parent hydrates slim list rows
    // with the full document (details etc.) shortly after selection -- but
    // never clobber in-progress edits on the same property.
    const sameProperty = Boolean(propertyInfo?.id && propertyInfo.id === property.id);
    if (sameProperty && dirty) return;
    setPropertyInfo(property);
    if (!sameProperty) setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const updatePropertyInfo = useCallback((path, value) => {
    setPropertyInfo((prev) => deepSet(prev || {}, path, value));
    setDirty(true);
  }, []);

  const handleCollapseStates = useCallback((states) => {
    setCollapseStates((prev) => ({ ...prev, ...states }));
  }, []);

  const allCollapsed = Object.keys(collapseStates).length > 0 &&
    Object.values(collapseStates).every((v) => v === true);

  const handleSave = useCallback(() => {
    if (!dirty || !onSave || !propertyInfo) return;
    onSave(propertyInfo);
    setDirty(false);
  }, [dirty, onSave, propertyInfo]);

  const handlePanelClose = useCallback(() => {
    if (dirty) {
      const confirmed = window.confirm("You have unsaved changes. Close and lose your changes?");
      if (!confirmed) return;
    }
    onClose?.();
  }, [dirty, onClose]);

  const titleLabel = formatOverviewAddressHeader(propertyInfo?.identity?.address || {})
    || propertyInfo?.name
    || propertyInfo?.identity?.apn
    || "Property";

  if (!property) {
    return (
      <AddressbookPanelShell
        onClose={handlePanelClose}
        titleContent={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#1D4ED8" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Details</Typography>
          </Box>
        }
      >
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
            Select a property to view details.
          </Typography>
        </Box>
      </AddressbookPanelShell>
    );
  }

  const detailBody = (
    <Box sx={{ p: 1 }}>
      <PropertyOverviewDetailsTab
        propertyInfo={propertyInfo}
        updatePropertyInfo={updatePropertyInfo}
        allCardsCollapsed={allCollapsed}
        onCollapseChange={handleCollapseStates}
        setAllSignal={setAllSignal}
        requestSetAllCards={requestSetAllCards}
      />
      <PropertyInfoDetailsTab
        propertyInfo={propertyInfo}
        updatePropertyInfo={updatePropertyInfo}
        allCardsCollapsed={allCollapsed}
        onCollapseChange={handleCollapseStates}
        setAllSignal={setAllSignal}
        requestSetAllCards={requestSetAllCards}
      />
      <PropertyHistoryCards
        propertyInfo={propertyInfo}
        updatePropertyInfo={updatePropertyInfo}
        allCardsCollapsed={allCollapsed}
        onCollapseChange={handleCollapseStates}
        setAllSignal={setAllSignal}
        requestSetAllCards={requestSetAllCards}
      />
      <PropertyLoansAndRentalCards
        propertyInfo={propertyInfo}
        updatePropertyInfo={updatePropertyInfo}
        allCardsCollapsed={allCollapsed}
        onCollapseChange={handleCollapseStates}
        setAllSignal={setAllSignal}
        requestSetAllCards={requestSetAllCards}
      />
      {propertyInfo?.id ? (
        <Box sx={{ mt: 1.5 }}>
          <LinkedDocumentsCard refCollection="propertyAssets" refId={propertyInfo.id} />
        </Box>
      ) : null}
    </Box>
  );

  if (embedded) {
    return detailBody;
  }

  return (
    <AddressbookPanelShell
      onClose={handlePanelClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#1D4ED8" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {titleLabel}
          </Typography>
        </Box>
      }
      headerActions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={!dirty}
            title="Save"
            sx={{
              "&&": {
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "1px solid",
                borderColor: dirty ? "#16a34a" : "#E5E7EB",
                backgroundColor: dirty ? "#16a34a" : "#E5E7EB",
                color: dirty ? "#FFFFFF" : "#9CA3AF",
                "&:hover": {
                  backgroundColor: dirty ? "#15803d" : "#E5E7EB",
                  borderColor: dirty ? "#15803d" : "#E5E7EB",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#E5E7EB",
                  borderColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              },
            }}
          >
            <SaveIcon sx={{ fontSize: 14 }} />
          </IconButton>
          {onCopy && property?.id && (
            <IconButton
              size="small"
              onClick={() => onCopy(property)}
              title="Copy property"
              sx={{
                "&&": {
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: "1px solid #BFDBFE",
                  backgroundColor: "#FFFFFF",
                  color: "#2563EB",
                  "&:hover": { backgroundColor: "#DBEAFE", borderColor: "#93C5FD" },
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
          <Tooltip title={allCollapsed ? "Expand all" : "Collapse all"}>
            <IconButton
              size="small"
              onClick={() => requestSetAllCards(!allCollapsed)}
              data-shell-muted="true"
              sx={{ width: 24, height: 24 }}
            >
              {allCollapsed
                ? <UnfoldMoreIcon sx={{ fontSize: 15 }} />
                : <UnfoldLessIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      }
    >
      {detailBody}
    </AddressbookPanelShell>
  );
};

export default PropertyDetailPanel;
